import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  RefreshCw, DollarSign, Clock, CheckCircle, XCircle,
  Loader2, AlertTriangle, Banknote, Copy, Upload, FileText, Paperclip,
} from 'lucide-react';

// Status interno → label visual (operacional/profissional).
// NÃO altera nada no backend, é puramente apresentação.
const STATUS_CONFIG = {
  PENDING:  { label: 'Pendente',                 color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock },
  APPROVED: { label: 'Aprovado para pagamento',  color: 'bg-blue-100 text-blue-700 border-blue-200',       icon: CheckCircle },
  PAID:     { label: 'Transferência concluída',  color: 'bg-green-100 text-green-700 border-green-200',    icon: Banknote },
  REJECTED: { label: 'Rejeitado',                color: 'bg-red-100 text-red-700 border-red-200',          icon: XCircle },
};

const PIX_TYPE_LABEL = {
  cpf: 'CPF',
  cnpj: 'CNPJ',
  email: 'E-mail',
  phone: 'Telefone',
  random: 'Chave aleatória',
};

const MAX_PROOF_SIZE = 5 * 1024 * 1024;
const ACCEPTED_PROOF = 'image/jpeg,image/jpg,image/png,image/webp,application/pdf';

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const getInitials = (name) => {
  if (!name) return '??';
  return name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase();
};

export default function AdminPayoutRequests() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');

  // Campos de auditoria preenchidos no modal
  const [txid, setTxid] = useState('');
  const [notes, setNotes] = useState('');
  const [proofUrl, setProofUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  // Refetch quando filtro muda
  useEffect(() => {
    if (user && user.user_type === 'admin') {
      fetchRequests(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  // Reset campos de auditoria ao trocar de request
  useEffect(() => {
    setTxid(selectedRequest?.txid || '');
    setNotes(selectedRequest?.notes || '');
    setProofUrl(selectedRequest?.proof_url || '');
  }, [selectedRequest]);

  const fetchRequests = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);

      const res = await axios.get(`${API}/admin/payout-requests?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(res.data.requests || []);
      setCounts(res.data.counts || {});
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar solicitações');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API}/admin/payout-requests/${selectedRequest.id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setSelectedRequest(null);
      fetchRequests(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Tem certeza que deseja rejeitar esta solicitação?')) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/payout-requests/${selectedRequest.id}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Solicitação rejeitada');
      setSelectedRequest(null);
      fetchRequests(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao rejeitar');
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!selectedRequest) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API}/admin/payout-requests/${selectedRequest.id}/mark-paid`,
        {
          txid: txid?.trim() || null,
          notes: notes?.trim() || null,
          proof_url: proofUrl?.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setSelectedRequest(null);
      fetchRequests(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao marcar como pago');
    } finally {
      setProcessing(false);
    }
  };

  const handleCopyPix = async () => {
    const key = selectedRequest?.payout_data?.pix_key;
    if (!key) {
      toast.error('Chave Pix não cadastrada');
      return;
    }
    try {
      await navigator.clipboard.writeText(key);
      toast.success('Chave Pix copiada');
    } catch {
      toast.error('Não foi possível copiar');
    }
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !selectedRequest) return;

    if (file.size > MAX_PROOF_SIZE) {
      toast.error('Arquivo excede 5MB');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/admin/payout-requests/${selectedRequest.id}/upload-proof`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setProofUrl(res.data.proof_url);
      toast.success('Comprovante anexado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar comprovante');
    } finally {
      setUploading(false);
    }
  };

  // Atualização de auditoria (TXID, notas, comprovante) após status PAID.
  // NÃO altera status nem valor — apenas registros operacionais com trilha auditável.
  const handleSaveAudit = async () => {
    if (!selectedRequest) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/payout-requests/${selectedRequest.id}/audit`,
        { txid, notes, proof_url: proofUrl || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Auditoria atualizada');
      await fetchRequests(true);
      // Atualiza o request selecionado localmente para refletir mudanças
      setSelectedRequest((prev) => prev && ({
        ...prev,
        txid: txid || prev.txid,
        notes: notes || prev.notes,
        proof_url: proofUrl || prev.proof_url,
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar auditoria');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Solicitações de Repasse" subtitle="Carregando...">
        <div className="space-y-6">
          <Skeleton className="h-24" />
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}
          </div>
        </div>
      </AdminLayout>
    );
  }

  const statusConfigForSelected = selectedRequest
    ? STATUS_CONFIG[selectedRequest.status] || STATUS_CONFIG.PENDING
    : null;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" data-testid="payout-requests-title">
              Solicitações de Repasse
            </h1>
            <p className="text-slate-500">Gerenciamento de solicitações de repasse dos prestadores</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchRequests(true)} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="p-4 border-l-4 border-l-yellow-500">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg"><Clock className="w-5 h-5 text-yellow-600" /></div>
              <div>
                <p className="text-2xl font-bold">{counts.PENDING || 0}</p>
                <p className="text-xs text-slate-500">Pendentes</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-l-4 border-l-blue-500">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><CheckCircle className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold">{counts.APPROVED || 0}</p>
                <p className="text-xs text-slate-500">Aprovados</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-l-4 border-l-green-500">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg"><Banknote className="w-5 h-5 text-green-600" /></div>
              <div>
                <p className="text-2xl font-bold">{counts.PAID || 0}</p>
                <p className="text-xs text-slate-500">Pagos</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-l-4 border-l-red-500">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg"><XCircle className="w-5 h-5 text-red-600" /></div>
              <div>
                <p className="text-2xl font-bold">{counts.REJECTED || 0}</p>
                <p className="text-xs text-slate-500">Rejeitados</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={filterStatus === '' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setFilterStatus(''); fetchRequests(true); }}
            >
              Todos
            </Button>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <Button
                key={key}
                variant={filterStatus === key ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setFilterStatus(key); fetchRequests(true); }}
              >
                {config.label}
              </Button>
            ))}
          </div>
        </Card>

        {/* Requests List */}
        <div className="space-y-3">
          {requests.length > 0 ? (
            requests.map((req) => {
              const statusConfig = STATUS_CONFIG[req.status] || STATUS_CONFIG.PENDING;
              const StatusIcon = statusConfig.icon;
              return (
                <Card
                  key={req.id}
                  className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedRequest(req)}
                  data-testid={`payout-request-${req.id}`}
                >
                  <div className="flex items-center gap-4">
                    <Avatar className="w-12 h-12">
                      <AvatarFallback className="bg-green-100 text-green-700">
                        {getInitials(req.provider?.business_name || req.provider?.name || req.provider_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-semibold">
                        {req.provider?.business_name || req.provider?.name || req.provider_name}
                      </p>
                      <p className="text-sm text-slate-500">{req.provider?.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">{formatCurrency(req.amount)}</p>
                      <p className="text-xs text-slate-400">{formatDate(req.created_at)}</p>
                    </div>
                    <Badge className={statusConfig.color}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {statusConfig.label}
                    </Badge>
                    {req.status === 'PENDING' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setSelectedRequest(req); }}
                      >
                        Revisar
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })
          ) : (
            <Card className="p-12 text-center">
              <DollarSign className="w-16 h-16 mx-auto mb-4 text-slate-200" />
              <h3 className="text-lg font-semibold mb-2">Nenhuma solicitação encontrada</h3>
              <p className="text-slate-500">As solicitações de repasse dos prestadores aparecerão aqui</p>
            </Card>
          )}
        </div>

        {requests.length > 0 && (
          <p className="text-sm text-slate-500 text-center">
            Mostrando {requests.length} solicitação(ões)
          </p>
        )}
      </div>

      {/* Modal de Detalhes */}
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Solicitação de Repasse
            </DialogTitle>
            <DialogDescription>Revise e aprove ou rejeite esta solicitação</DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4 py-4">
              {/* Prestador */}
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-green-100 text-green-700">
                      {getInitials(selectedRequest.provider?.business_name || selectedRequest.provider?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold">
                      {selectedRequest.provider?.business_name || selectedRequest.provider?.name || selectedRequest.provider_name}
                    </p>
                    <p className="text-sm text-slate-500">{selectedRequest.provider?.email}</p>
                  </div>
                </div>
              </Card>

              {/* Valor */}
              <Card className="p-4 bg-green-50 border-green-200">
                <p className="text-sm text-green-700 mb-1">Valor Solicitado</p>
                <p className="text-3xl font-bold text-green-700">{formatCurrency(selectedRequest.amount)}</p>
              </Card>

              {/* Dados para Repasse (Pix) */}
              <Card className="p-4 border-slate-200" data-testid="payout-pix-block">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-700">Dados para Repasse</p>
                  {selectedRequest.payout_data?.pix_key && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyPix}
                      data-testid="copy-pix-key-btn"
                    >
                      <Copy className="w-3.5 h-3.5 mr-1.5" />
                      Copiar Chave Pix
                    </Button>
                  )}
                </div>
                {selectedRequest.payout_data ? (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-slate-500">Tipo</p>
                      <p className="font-medium">{PIX_TYPE_LABEL[selectedRequest.payout_data.pix_type] || selectedRequest.payout_data.pix_type || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Chave Pix</p>
                      <p className="font-mono font-medium break-all" data-testid="pix-key-value">
                        {selectedRequest.payout_data.pix_key || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Titular</p>
                      <p className="font-medium">{selectedRequest.payout_data.holder_name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Documento</p>
                      <p className="font-medium font-mono">{selectedRequest.payout_data.holder_document || '—'}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>O prestador ainda não cadastrou os dados Pix de recebimento.</span>
                  </div>
                )}
              </Card>

              {/* Status atual */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Status:</span>
                <Badge className={statusConfigForSelected.color}>
                  {statusConfigForSelected.label}
                </Badge>
              </div>

              {/* Datas */}
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-500">Solicitado em:</span>
                  <span>{formatDate(selectedRequest.created_at)}</span>
                </div>
                {selectedRequest.processed_at && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Processado em:</span>
                    <span>{formatDate(selectedRequest.processed_at)}</span>
                  </div>
                )}
                {selectedRequest.paid_at && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Transferido em:</span>
                    <span>{formatDate(selectedRequest.paid_at)}</span>
                  </div>
                )}
              </div>

              {/* Bloco de auditoria — visível quando aprovado ou pago */}
              {(selectedRequest.status === 'APPROVED' || selectedRequest.status === 'PAID') && (
                <Card className="p-4 border-slate-200" data-testid="payout-audit-block">
                  <p className="text-sm font-semibold text-slate-700 mb-3">Auditoria do Pagamento</p>

                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">TXID / Código da transferência</label>
                      <Input
                        value={txid}
                        onChange={(e) => setTxid(e.target.value)}
                        placeholder="Ex: E18236187263817263"
                        data-testid="payout-txid-input"
                      />
                    </div>

                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Observações internas</label>
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Notas para suporte / antifraude / auditoria"
                        rows={3}
                        data-testid="payout-notes-input"
                      />
                    </div>

                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Comprovante</label>
                      {proofUrl ? (
                        <div className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                          <FileText className="w-4 h-4 text-slate-600" />
                          <a
                            href={proofUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline truncate"
                            data-testid="payout-proof-link"
                          >
                            Ver comprovante anexado
                          </a>
                          {selectedRequest.status === 'APPROVED' && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="ml-auto text-xs"
                              onClick={() => setProofUrl('')}
                            >
                              Remover
                            </Button>
                          )}
                        </div>
                      ) : (selectedRequest.status === 'APPROVED' || selectedRequest.status === 'PAID') ? (
                        <>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept={ACCEPTED_PROOF}
                            className="hidden"
                            onChange={handleProofUpload}
                            data-testid="payout-proof-file"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            data-testid="payout-proof-btn"
                          >
                            {uploading ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Paperclip className="w-4 h-4 mr-2" />
                            )}
                            Anexar comprovante
                          </Button>
                          <p className="text-[11px] text-slate-400 mt-1">
                            JPG, PNG, WEBP ou PDF (máx 5MB).
                          </p>
                        </>
                      ) : (
                        <span className="text-sm text-slate-400">Sem comprovante.</span>
                      )}
                    </div>
                  </div>
                </Card>
              )}

              {/* Alertas contextuais */}
              {selectedRequest.status === 'PENDING' && (
                <Card className="p-3 bg-amber-50 border-amber-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                    <p className="text-sm text-amber-700">
                      Ao aprovar, todos os serviços disponíveis do prestador serão marcados como "pago".
                    </p>
                  </div>
                </Card>
              )}

              {selectedRequest.status === 'APPROVED' && (
                <Card className="p-3 bg-blue-50 border-blue-200">
                  <div className="flex items-start gap-2">
                    <Banknote className="w-4 h-4 text-blue-600 mt-0.5" />
                    <p className="text-sm text-blue-700">
                      Este repasse está aprovado para pagamento. Após realizar o Pix, preencha os dados de auditoria e clique em "Marcar como Pago".
                    </p>
                  </div>
                </Card>
              )}

              {selectedRequest.status === 'PAID' && (
                <Card className="p-3 bg-emerald-50 border-emerald-200">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5" />
                    <p className="text-sm text-emerald-700">
                      Transferência concluída. Você pode <strong>atualizar TXID, observações e anexar comprovante</strong> para fins de auditoria — o status financeiro permanece como Pago.
                    </p>
                  </div>
                </Card>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSelectedRequest(null)}>Fechar</Button>

            {selectedRequest?.status === 'PENDING' && (
              <>
                <Button variant="destructive" onClick={handleReject} disabled={processing} data-testid="reject-payout-btn">
                  {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                  Rejeitar
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleApprove}
                  disabled={processing}
                  data-testid="approve-payout-btn"
                >
                  {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Aprovar Repasse
                </Button>
              </>
            )}

            {selectedRequest?.status === 'APPROVED' && (
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={handleMarkAsPaid}
                disabled={processing}
                data-testid="mark-paid-btn"
              >
                {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Marcar como Pago
              </Button>
            )}

            {selectedRequest?.status === 'PAID' && (
              <Button
                variant="outline"
                onClick={handleSaveAudit}
                disabled={processing}
                data-testid="save-audit-btn"
              >
                {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Salvar Auditoria
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
